import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontSourceSerif4, setFullViewportWidthStyles, theme } from "shared/lib/styles";
import type { WithStickyStackedHeightProps } from "shared/lib/styles";

import {
  AdSlotContainer,
  StyledSlot,
} from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import { ContentItemSeriesBanner } from "scmp-app/components/content/content-item-render/variants/series-banner";

import { SeriesArticleOtherScmpSeries } from "./other-scmp-series";

export const Container = styled.div`
  ${setFullViewportWidthStyles()}
  padding: 20px;
  ${theme.breakpoints.up("tablet")} {
    padding: 40px;
  }
`;

export const BannerAdSlot = styled.div`
  grid-area: top-banner;

  ${theme.breakpoints.up("desktop")} {
    ${setFullViewportWidthStyles()}
  }
`;

export const Wrapper = styled.div`
  display: grid;
  grid:
    "." 20px
    "banner" min-content
    "." 20px
    "top-banner" min-content
    "." 28px
    "list"
    / minmax(0, 600px);
  justify-content: center;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "." 40px
      "banner" min-content
      "." 32px
      "top-banner" min-content
      "." 32px
      "list"
      "." 32px
      "other-series" min-content
      / minmax(0, 800px);
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      "top-banner top-banner top-banner top-banner" min-content
      ". . . ." 40px
      "banner banner banner banner" min-content
      ". . . ." 32px
      ". list .  right" min-content
      ". . . ." 40px
      "other-series other-series other-series other-series" min-content
      / 60px 1fr 56px 300px;

    max-inline-size: 1024px;
    margin-inline: auto;
  }

  :has(${BannerAdSlot} [data-slot-collapse="true"]) {
    ${theme.breakpoints.down("desktop")} {
      grid:
        "." 20px
        "banner" min-content
        "." 28px
        "list"
        / minmax(0, 600px);

      ${theme.breakpoints.up("tablet")} {
        grid:
          "." 40px
          "banner" min-content
          "." 32px
          "list"
          "." 32px
          "other-series" min-content
          / minmax(0, 800px);
      }

      ${BannerAdSlot} {
        display: none;
      }
    }
  }
`;

type ItemContainerProps = {
  $isAds?: boolean;
};

export const ItemContainer = styled.div<ItemContainerProps>`
  ${props =>
    props.$isAds &&
    css`
      display: flex;
      justify-content: center;
      align-items: center;
    `}

  ${theme.breakpoints.up("desktop")} {
    display: ${props => (props.$isAds ? "none" : "block")};
  }
`;

type ListProps = {
  $isReverseOrder?: boolean;
};
export const List = styled.div<ListProps>`
  grid-area: list;

  display: flex;

  inline-size: 100%;
  max-inline-size: 400px;
  margin-inline: auto;
  padding-inline: 0;

  ${theme.breakpoints.up("tablet")} {
    max-inline-size: 608px;
  }
  ${theme.breakpoints.up("desktop")} {
    max-inline-size: none;
    padding-inline: 0;
  }

  ${props =>
    props.$isReverseOrder
      ? css`
          flex-direction: column-reverse;
          ${ItemContainer} {
            &:not(:first-child) {
              margin-block-end: 16px;
              padding-block-end: 16px;
              border-block-end: 1px solid #dfdfdf;
            }
          }
        `
      : css`
          flex-direction: column;
          ${ItemContainer} {
            &:not(:last-child) {
              margin-block-end: 16px;
              padding-block-end: 16px;
              border-block-end: 1px solid #dfdfdf;
            }
          }
        `}
`;

export const SeriesItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${theme.breakpoints.up("tablet")} {
    flex-direction: row;
  }
`;

export const SeriesNumber = styled.div`
  color: #000000;
  font-weight: 500;
  font-size: 24px;
  font-family: ${fontSourceSerif4};
  font-style: italic;
  font-variant-numeric: lining-nums proportional-nums;

  line-height: normal;

  span {
    padding-inline: 2px;

    text-align: center;
  }

  ${theme.breakpoints.up("tablet")} {
    margin-inline-end: 26px;
  }
`;

export const MobileAndTabletAdSlot = styled.div`
  display: block;

  ${theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const DesktopAdSlot = styled.div`
  display: none;

  ${theme.breakpoints.up("tablet")} {
    display: block;
  }
`;

export const StyledSeriesArticleOtherScmpSeries = styled(SeriesArticleOtherScmpSeries)`
  grid-area: other-series;

  display: none;

  ${theme.breakpoints.up("tablet")} {
    display: grid;
  }
`;

export const StyledContentItemSeriesBanner = styled(ContentItemSeriesBanner)`
  grid-area: banner;
`;

export const RightSectionSticky = styled.div<WithStickyStackedHeightProps>`
  display: none;

  ${theme.breakpoints.up("desktop")} {
    display: block;
    flex: auto;
  }

  && ${AdSlotContainer} {
    display: flex;
    align-items: flex-start;

    block-size: 100%;

    ${StyledSlot} {
      position: sticky;
      inset-block-start: ${props => props.$stickyStackedHeight + 50}px;

      block-size: auto;
    }
  }
`;

export const RightSection = styled.div`
  flex: auto;
`;

export const Right = styled.div`
  grid-area: right;

  display: none;
  flex-direction: column;
  gap: 40px;
  ${theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;
